import { selectorFamily } from "recoil"
import { CustomAlert } from "src/utils/CustomAlert"
import { contactReplyThreadsRequestIdState } from "src/recoil/atoms/contact/contactReplyThreadsRequestIdState"
import { contactDetailCacheSkipState } from "src/recoil/atoms/contact/contactDetailCacheSkipState"
import { selectedMyOrganizationSelector } from "src/recoil/selectors/organization/selectedMyOrganizationSelector"
import { myProfileSelectorFamily } from "src/recoil/selectors/organization/myProfile/myProfileSelectorFamily"
import { getContactReplyThreads } from "src/apis/contact/getContactReplyThreads"
import { requestDataSelector } from "src/recoil/selectors/auth/requestDataSelector"
import { ContactListCacheService } from "src/services/cache/ContactListCacheService"

export type ContactReplyThreadOverview = {
  replyThreadId: string
  partner: {
    id: string
    name: string
    isDeleted?: boolean
  }
  recentMessage: {
    text: string
    updatedAt: Date
  }
  unreadMessageCount: number
}

export const contactReplyThreadsSelectorFamily = selectorFamily<
  ContactReplyThreadOverview[],
  { contactId: string | undefined; viewMode?: "Inbox" | "Outbox" | "Reserved" | "Migration" }
>({
  key: "selectors/contact/contactReplyThreadsSelectorFamily",
  get:
    ({ contactId, viewMode }) =>
    async ({ get }) => {
      if (!(viewMode === "Inbox" || viewMode === "Outbox")) return []
      // requestIdを監視（リフレッシュ時にRecoilが再評価をトリガー）
      const requestId = get(contactReplyThreadsRequestIdState)
      // キャッシュスキップフラグを監視（Pull-to-Refresh時にキャッシュをスキップ）
      const skipCache = get(contactDetailCacheSkipState)
      if (contactId == null) return []

      // キャッシュチェック
      // skipCache: 明示的なキャッシュスキップフラグ（Pull-to-Refresh時にtrue）
      // requestId: キャッシュ保存時のrequestIdと比較してキャッシュの有効性を判断
      const cached = ContactListCacheService.getReplyThreads({
        contactId,
        viewMode,
      })

      // キャッシュを使用する条件:
      // 1. skipCacheがfalse（初回ロード時/Pull-to-Refresh時はtrue）
      // 2. キャッシュが存在する
      // Note: skipCacheのデフォルト値はtrue。初回ロード完了後にfalseに設定される。
      //       これによりブラウザリロード時は常に最新データをAPIから取得する。
      if (!skipCache && cached) {
        return cached.data
      }

      const requestData = get(requestDataSelector)
      if (requestData == null) return []
      const { accountId, accessToken } = requestData

      const organization = get(selectedMyOrganizationSelector)
      const organizationId = organization?.id
      if (organizationId == null) {
        return []
      }
      const myProfile = get(myProfileSelectorFamily(organizationId))
      const myMemberId = myProfile?.memberId
      if (myMemberId == null) {
        return []
      }

      const result = await getContactReplyThreads({ accessToken, accountId, contactId, myMemberId, viewMode })

      if (!result.isOk) {
        CustomAlert.alert("エラー", result.error.message)
        return []
      }

      // キャッシュに保存
      ContactListCacheService.setReplyThreads({ contactId, viewMode }, result.content)

      return result.content
    },
})
